import {FC, useEffect, useMemo, useRef} from 'react'
import classNames from 'classnames'
import {useSelector} from 'react-redux'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {isProductivityDisabledForPrimaryDomain} from 'definitions/constant/analyticsProductivityTenantGate'
import {selectUser} from 'features/User'
import {usePolicy} from 'hooks/usePolicy'
import {useToggle} from 'hooks/useToggle'
import {TENANT_NO_STORAGE_RESIDENCY_REGION} from 'definitions/tenantConfig'
import {useTenantConfigQuery} from 'hooks/useTenantConfigQuery'
import {stringSplitAndJoin} from 'utils'
import {ChevronRightIcon} from '../SvgIcon'
import {TooltipBody, TooltipWrapper} from '../TooltipWrapper'
import {AnalyticsRoutesEnum} from 'interfaces'

const routes = [
  {name: 'Overview', path: AnalyticsRoutesEnum.ANALYTICS_OVERVIEW},
  {name: 'Policy', path: AnalyticsRoutesEnum.ANALYTICS_POLICY},
  {name: 'Productivity', path: AnalyticsRoutesEnum.ANALYTICS_PRODUCTIVITY},
  {name: 'Shadow IT', path: AnalyticsRoutesEnum.ANALYTICS_SHADOW_IT},
  {name: 'Detail', path: AnalyticsRoutesEnum.ANALYTICS_DETAIL},
]

const ANALYTICS_DROPDOWN_TOOLTIP_ID = 'analytics-route-dropdown-tooltip'
const ANALYTICS_DROPDOWN_TENANT_TOOLTIP_ID =
  'analytics-route-dropdown-tenant-tooltip'

interface Props {
  bgColor: string
  textColor: string
  text: string
  bgOpacity: string
  bgHoverOpacity: string
  className?: string
}

export const AnalyticsRouteDropDown: FC<Props> = ({
  bgColor,
  text,
  textColor,
  bgOpacity,
  bgHoverOpacity,
  className,
}) => {
  const {t} = useTranslation(['common'])
  const {pathname} = usePolicy()
  const {toggleOn, onToggleClick} = useToggle()
  const ref = useRef<HTMLDivElement>(null)
  const {primaryDomain} = useSelector(selectUser)
  const tenantConfigQuery = useTenantConfigQuery()
  const isProductivityDomainBlocked = useMemo(
    () => isProductivityDisabledForPrimaryDomain(primaryDomain),
    [primaryDomain],
  )
  const isNoStorageTenantResidency = useMemo(
    () =>
      tenantConfigQuery.isSuccess &&
      tenantConfigQuery.data?.data?.residencyRegion ===
        TENANT_NO_STORAGE_RESIDENCY_REGION,
    [tenantConfigQuery.data, tenantConfigQuery.isSuccess],
  )
  const isTenantRegionalGatePending = useMemo(
    () =>
      !tenantConfigQuery.isSuccess &&
      (tenantConfigQuery.isLoading || tenantConfigQuery.isFetching),
    [
      tenantConfigQuery.isFetching,
      tenantConfigQuery.isLoading,
      tenantConfigQuery.isSuccess,
    ],
  )

  useEffect(() => {
    // to determine clicked target and close the dropdown
    const checkIfClickedOutside = (event: MouseEvent) => {
      if (
        toggleOn &&
        ref.current &&
        !ref.current.contains(event.target as Node)
      ) {
        onToggleClick()
      }
    }

    // adding event listener to know whenever user clicks on page
    document.addEventListener('mousedown', checkIfClickedOutside)

    // cleanup function to remove event listener on unmount
    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside)
    }
  }, [toggleOn])

  return (
    <div className="relative" ref={ref}>
      <button
        data-cy="analytics-dropdown"
        onClick={onToggleClick}
        tabIndex={0}
        className={classNames(
          // Combine all the dynamic classes as a single string rather than multiple arguments
          `bg-${bgColor} bg-${bgOpacity} hover:bg-${bgHoverOpacity} border-${bgColor}`,
          // Fixed classes
          'outline-none rounded-full flex items-center justify-center border focus-visible:border-white px-4 py-1 ml-3',
          // Separate argument for the custom className prop
          className,
        )}
      >
        <p className={`text-sm karla-medium ${textColor}`}>{text}</p>
        <ChevronRightIcon
          strokeColor={textColor}
          classes={classNames('transform rotate-90 ml-4', {
            '-rotate-90': toggleOn,
          })}
          width={6}
          height={9}
        />
      </button>

      <div
        className={classNames(
          'absolute z-50 bg-dope-black border border-gray-600 flex flex-col w-40 mt-3 mx-auto',
          {
            hidden: toggleOn === false,
          },
        )}
      >
        {routes.map((data, idx) => {
          const isProductivityRoute =
            data.path === AnalyticsRoutesEnum.ANALYTICS_PRODUCTIVITY
          const isShadowItRoute =
            data.path === AnalyticsRoutesEnum.ANALYTICS_SHADOW_IT
          const isProductivityDisabled =
            isProductivityRoute &&
            (isNoStorageTenantResidency ||
              isTenantRegionalGatePending ||
              isProductivityDomainBlocked)
          const isShadowItDisabled =
            isShadowItRoute &&
            (isNoStorageTenantResidency || isTenantRegionalGatePending)
          const isRouteDisabled = isProductivityDisabled || isShadowItDisabled

          if (isRouteDisabled) {
            let disabledRouteTooltipProps: Record<string, string> = {}
            if (isNoStorageTenantResidency) {
              disabledRouteTooltipProps = {
                'data-tooltip-content': t(
                  'NO_STORAGE_REGION_RESTRICTED_TOOLTIP',
                ),
                'data-tooltip-id': ANALYTICS_DROPDOWN_TOOLTIP_ID,
              }
            } else if (isProductivityRoute && isProductivityDomainBlocked) {
              disabledRouteTooltipProps = {
                'data-tooltip-content': t(
                  'ANALYTICS_TENANT_UNAVAILABLE_TOOLTIP',
                ),
                'data-tooltip-id': ANALYTICS_DROPDOWN_TENANT_TOOLTIP_ID,
              }
            }

            return (
              <button
                key={`analytics-route-item-${idx}`}
                data-cy={`analytics-dropdown-option-${stringSplitAndJoin(
                  data?.name?.toLowerCase(),
                )}`}
                {...disabledRouteTooltipProps}
                disabled
                className={classNames('w-full py-2 karla-light text-sm', {
                  'border-b border-gray-700': idx !== routes.length - 1,
                  'cursor-not-allowed text-white/40': true,
                })}
              >
                {data.name}
              </button>
            )
          }

          return (
            <Link
              className="w-full"
              to={data.path}
              key={`analytics-route-item-${idx}`}
            >
              <button
                data-cy={`analytics-dropdown-option-${stringSplitAndJoin(
                  data?.name?.toLowerCase(),
                )}`}
                className={classNames(
                  'w-full py-2 karla-light text-sm hover:bg-gray-800',
                  {
                    'border-b border-gray-700': idx !== routes.length - 1,
                    [textColor]: data.path === pathname,
                  },
                )}
              >
                {data.name}
              </button>
            </Link>
          )
        })}
      </div>
      {isNoStorageTenantResidency ? (
        <TooltipWrapper
          id={ANALYTICS_DROPDOWN_TOOLTIP_ID}
          place="bottom"
          className="z-tooltip"
          render={({content}) => (
            <TooltipBody classes="shadow-none" noPadding>
              {content}
            </TooltipBody>
          )}
        />
      ) : null}
      {isProductivityDomainBlocked ? (
        <TooltipWrapper
          id={ANALYTICS_DROPDOWN_TENANT_TOOLTIP_ID}
          place="bottom"
          className="z-tooltip"
          render={({content}) => (
            <TooltipBody classes="shadow-none" noPadding>
              {content}
            </TooltipBody>
          )}
        />
      ) : null}
    </div>
  )
}
